import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { client } from './api-client';

// Create new maintenance log
export const useCreateMaintenanceLog = () => {
  const queryClient = useQueryClient();
  
  const createMaintenanceLog = async (maintenanceData) => {
    const response = await client('create_maintenance_log', {
      method: 'POST',
      data: maintenanceData,
    });
    
    if (response?.error) {
      throw new Error(response.error);
    }
    
    return response;
  };
  
  return useMutation(createMaintenanceLog, {
    onSuccess: (data, variables) => {
      console.log('✅ Maintenance log created successfully:', data);
      
      // Invalidate maintenance logs for the specific vehicle
      queryClient.invalidateQueries(['maintenance-logs', variables.vehicle_id]);
    },
    onError: (error) => {
      console.error('❌ Failed to create maintenance log:', error.message);
    },
  });
};

// Get vehicle maintenance logs
export const useGetVehicleMaintenanceLogs = (vehicleId) => {
  return useQuery(
    ['maintenance-logs', vehicleId],
    async () => {
      if (!vehicleId) {
        return [];
      }
      
      const searchParams = new URLSearchParams({
        vehicle_id: vehicleId,
      });
      
      const response = await client(`read_vehicle_maintenance_logs?${searchParams.toString()}`);
      
      if (response?.error) {
        throw new Error(response.error);
      }
      
      return response || [];
    },
    {
      enabled: !!vehicleId,
      keepPreviousData: true,
    }
  );
};

// Update maintenance log
export const useUpdateMaintenanceLog = (vehicleId) => {
  const queryClient = useQueryClient();
  
  const updateMaintenanceLog = async ({ logId, updateData }) => {
    const searchParams = new URLSearchParams({
      log_id: logId,
    });
    
    const response = await client(`update_maintenance_log?${searchParams.toString()}`, {
      method: 'PUT',
      data: updateData,
    });
    
    if (response?.error) {
      throw new Error(response.error);
    }
    
    return response;
  };
  
  return useMutation(updateMaintenanceLog, {
    onSuccess: (data, variables) => {
      console.log('✅ Maintenance log updated successfully:', data);
      
      // Invalidate maintenance logs for the specific vehicle
      if (vehicleId) {
        queryClient.invalidateQueries(['maintenance-logs', vehicleId]);
      } else {
        // Fallback: invalidate all maintenance logs if vehicleId is not provided
        queryClient.invalidateQueries(['maintenance-logs']);
      }
    },
    onError: (error) => {
      console.error('❌ Failed to update maintenance log:', error.message);
    },
  });
};

// Delete maintenance log
export const useDeleteMaintenanceLog = (vehicleId) => {
  const queryClient = useQueryClient();
  
  const deleteMaintenanceLog = async (logId) => {
    const searchParams = new URLSearchParams({
      log_id: logId,
    });
    
    const response = await client(`delete_maintenance_log?${searchParams.toString()}`, {
      method: 'DELETE',
    });
    
    if (response?.error) {
      throw new Error(response.error);
    }
    
    return response;
  };
  
  return useMutation(deleteMaintenanceLog, {
    onSuccess: (data, logId) => {
      console.log('✅ Maintenance log deleted successfully:', data);
      
      // Invalidate maintenance logs for the specific vehicle
      if (vehicleId) {
        queryClient.invalidateQueries(['maintenance-logs', vehicleId]);
      } else {
        // Fallback: invalidate all maintenance logs if vehicleId is not provided
        queryClient.invalidateQueries(['maintenance-logs']);
      }
    },
    onError: (error) => {
      console.error('❌ Failed to delete maintenance log:', error.message);
    },
  });
};

// Create new maintenance record (legacy - kept for backward compatibility)
export const useCreateMaintenance = () => {
  const queryClient = useQueryClient();
  
  const createMaintenance = async (maintenanceData) => {
    const response = await client('maintenance', {
      method: 'POST',
      data: maintenanceData,
    });
    
    if (response?.error) {
      throw new Error(response.error);
    }
    
    return response;
  };
  
  return useMutation(createMaintenance, {
    onSuccess: (data) => {
      console.log('✅ Maintenance record created successfully:', data);
      
      // Invalidate maintenance list to refetch updated data
      queryClient.invalidateQueries(['maintenance']);
    },
    onError: (error) => {
      console.error('❌ Failed to create maintenance record:', error.message);
    },
  });
};

// Get all maintenance records with pagination
export const useGetMaintenance = (options = {}) => {
  const { skip = 0, limit = 10, ...otherOptions } = options;
  
  return useQuery(
    ['maintenance', { skip, limit, ...otherOptions }],
    async () => {
      const searchParams = new URLSearchParams({
        skip: skip.toString(),
        limit: limit.toString(),
        ...otherOptions
      });
      
      const response = await client(`maintenance?${searchParams.toString()}`);
      
      if (response?.error) {
        throw new Error(response.error);
      }
      
      return response;
    },
    {
      keepPreviousData: true,
    }
  );
};

// // Get single maintenance record by ID
// export const useGetMaintenanceRecord = (maintenanceId) => {
//   return useQuery(
//     ['maintenance', maintenanceId],
//     async () => {
//       const response = await client(`maintenance/${maintenanceId}`);
      
//       if (response?.error) {
//         throw new Error(response.error);
//       }
      
//       return response;
//     },
//     {
//       enabled: !!maintenanceId,
//       retry: 3,
//     }
//   );
// };

// // Update maintenance record
// export const useUpdateMaintenance = () => {
//   const queryClient = useQueryClient();
  
//   const updateMaintenance = async ({ maintenanceId, updateData }) => {
//     const response = await client(`maintenance/${maintenanceId}`, {
//       method: 'PUT',
//       data: updateData,
//     });
    
//     if (response?.error) {
//       throw new Error(response.error);
//     }
    
//     return response;
//   };
  
//   return useMutation(updateMaintenance, {
//     onSuccess: (data, variables) => {
//       console.log('✅ Maintenance record updated successfully:', data);
      
//       // Update the specific maintenance record in cache
//       queryClient.invalidateQueries(['maintenance', variables.maintenanceId]);
      
//       // Update maintenance list
//       queryClient.invalidateQueries(['maintenance']);
//     },
//     onError: (error) => {
//       console.error('❌ Failed to update maintenance record:', error.message);
//     },
//   });
// };

// // Delete maintenance record
// export const useDeleteMaintenance = () => {
//   const queryClient = useQueryClient();
  
//   const deleteMaintenance = async (maintenanceId) => {
//     const response = await client(`maintenance/${maintenanceId}`, {
//       method: 'DELETE',
//     });
    
//     if (response?.error) {
//       throw new Error(response.error);
//     }
    
//     return response;
//   };
  
//   return useMutation(deleteMaintenance, {
//     onSuccess: (data, maintenanceId) => {
//       console.log('✅ Maintenance record deleted successfully:', data);
      
//       // Remove from cache
//       queryClient.removeQueries(['maintenance', maintenanceId]);
      
//       // Invalidate maintenance list
//       queryClient.invalidateQueries(['maintenance']);
//     },
//     onError: (error) => {
//       console.error('❌ Failed to delete maintenance record:', error.message);
//     },
//   });
// };

// // Get maintenance records by vehicle ID
// export const useGetVehicleMaintenance = (vehicleId, options = {}) => {
//   const { skip = 0, limit = 10, ...otherOptions } = options;
  
//   return useQuery(
//     ['maintenance', 'vehicle', vehicleId, { skip, limit, ...otherOptions }],
//     async () => {
//       const searchParams = new URLSearchParams({
//         vehicle_id: vehicleId,
//         skip: skip.toString(),
//         limit: limit.toString(),
//         ...otherOptions
//       });
      
//       const response = await client(`maintenance?${searchParams.toString()}`);
      
//       if (response?.error) {
//         throw new Error(response.error);
//       }
      
//       return response;
//     },
//     {
//       enabled: !!vehicleId,
//       keepPreviousData: true,
//     }
//   );
// };

// // Get maintenance records by service type
// export const useGetMaintenanceByType = (serviceType, options = {}) => {
//   const { skip = 0, limit = 10, ...otherOptions } = options;
  
//   return useQuery(
//     ['maintenance', 'type', serviceType, { skip, limit, ...otherOptions }],
//     async () => {
//       const searchParams = new URLSearchParams({
//         service_type: serviceType,
//         skip: skip.toString(),
//         limit: limit.toString(),
//         ...otherOptions
//       });
      
//       const response = await client(`maintenance?${searchParams.toString()}`);
      
//       if (response?.error) {
//         throw new Error(response.error);
//       }
      
//       return response;
//     },
//     {
//       enabled: !!serviceType,
//       keepPreviousData: true,
//     }
//   );
// };

// Process service invoice image
export const useProcessServiceInvoice = () => {
  const processServiceInvoice = async ({ invoiceFile, vehicleId }) => {
    if (!vehicleId) {
      throw new Error('Vehicle ID is required');
    }
    
    const formData = new FormData();
    formData.append('invoice', invoiceFile);
    
    const searchParams = new URLSearchParams({
      vehicle_id: vehicleId,
    });
    
    const response = await client(`create_service_invoice_log?${searchParams.toString()}`, {
      method: 'POST',
      data: formData,
    });
    
    if (response?.error) {
      throw new Error(response.error);
    }
    
    return response;
  };
  
  return useMutation(processServiceInvoice, {
    onSuccess: (data) => {
      console.log('✅ Service invoice processed successfully:', data);
      // Response contains: service_date, mileage, service_type, notes, price
    },
    onError: (error) => {
      console.error('❌ Failed to process service invoice:', error.message);
    },
  });
};